/**
 * ELC module route definitions.
 *
 * Exports lazy-loaded page components and route paths for the
 * Employee Lifecycle module.  Consumed by the dynamic module route
 * loader in routes/index.tsx via import.meta.glob.
 *
 * Cross-References:
 *   - S-MOD.11: Frontend dynamic navigation
 *   - FR-MOD-001: Module Registry
 */
import { lazy, type ComponentType } from 'react';
import ElcSetupGate from './components/ElcSetupGate';

const WorkflowList = lazy(() => import('./pages/WorkflowList'));
const WorkflowDesigner = lazy(() => import('./pages/WorkflowDesigner'));
const WorkflowStatus = lazy(() => import('./pages/WorkflowStatus'));
const AccessRequest = lazy(() => import('./pages/AccessRequest'));
// Sprint E3-FO-A — request wizard, approver queue, request detail.
const AccessRequestNew = lazy(() => import('./pages/AccessRequestNew'));
const AccessRequestQueue = lazy(() => import('./pages/AccessRequestQueue'));
const AccessRequestDetail = lazy(() => import('./pages/AccessRequestDetail'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const ELCAdmin = lazy(() => import('./pages/admin/ELCAdmin'));
const ElcIntegrationsAdmin = lazy(() => import('./pages/ElcIntegrationsAdmin'));
const HrisPendingImports = lazy(() => import('./pages/HrisPendingImports'));
const HrisTriggers = lazy(() => import('./pages/HrisTriggers'));
const HrisFieldMappings = lazy(() => import('./pages/HrisFieldMappings'));
// Sprint ELC-5 — Role Catalog admin surface.
const RoleCatalog = lazy(() => import('./pages/RoleCatalog'));
// Sprint ELC-5.1 — Department Catalog admin surface.
const DepartmentCatalog = lazy(() => import('./pages/DepartmentCatalog'));
// FU5 — Location catalog admin surface.
const LocationCatalog = lazy(() => import('./pages/LocationCatalog'));
// E3-FO-D2 — Access Catalog admin surface.
const AccessCatalogAdmin = lazy(() => import('./pages/AccessCatalogAdmin'));
// E3-FO-E — ELC setup (prerequisite checklist + IT fulfilment group).
const ElcSetup = lazy(() => import('./pages/ElcSetup'));
// Sprint ELC-6 W3 — discrepancies queue (post-action verify + drift).
const ElcDiscrepancies = lazy(() => import('./pages/ElcDiscrepancies'));
// Sprint E3-M7 — cross-user × target provisioning matrix.
const RoleMatrix = lazy(() => import('./pages/RoleMatrix'));
// Sprint CHRON-2 — Form Builder + FormList admin surface.
const FormsAdmin = lazy(() => import('./pages/FormsAdmin'));
// Sprint CHRON-3 — Workflow Chronicle hub (supersedes WorkflowList).
const WorkflowChronicle = lazy(() => import('./pages/WorkflowChronicle'));

export const slug = 'elc';

/**
 * E3-FO-E — every ELC page except the setup page is wrapped in the setup
 * gate ("Finish ELC setup" while the tenant's setup is incomplete).
 */
const gated = (Page: ComponentType): ComponentType => {
  const Gated = () => (
    <ElcSetupGate>
      <Page />
    </ElcSetupGate>
  );
  return Gated;
};

const ungatedRoutes = [
  // E3-FO-E — where the gate sends admins; must stay reachable while setup is incomplete.
  { path: 'admin/setup', element: ElcSetup },
];

const gatedRoutes = [
  // CHRON-3: /workflows now serves Chronicle. Legacy WorkflowList
  // remains bundled + reachable via /workflows/legacy for one
  // release window while dashboards/deep-links migrate.
  { path: 'workflows', element: WorkflowChronicle },
  { path: 'workflows/legacy', element: WorkflowList },
  { path: 'workflows/:instanceId', element: WorkflowStatus },
  { path: 'designer', element: WorkflowDesigner },
  { path: 'access-requests', element: AccessRequest },
  { path: 'my-tasks', element: MyTasks },
  { path: 'access-requests/new', element: AccessRequestNew },
  { path: 'access-requests/queue', element: AccessRequestQueue },
  { path: 'access-requests/:requestId', element: AccessRequestDetail },
  { path: 'admin', element: ELCAdmin },
  // Sprint ELC-2 TKT-10 — privileged provisioning credentials admin.
  { path: 'admin/integrations', element: ElcIntegrationsAdmin },
  // Sprint ELC-3 — Rippling HRIS source admin surfaces.
  { path: 'admin/hris/pending', element: HrisPendingImports },
  { path: 'admin/hris/triggers', element: HrisTriggers },
  // Sprint ELC-3.1 — field mappings + org chart sync.
  { path: 'admin/hris/mappings', element: HrisFieldMappings },
  // Sprint ELC-5 — role catalog (ELC produces tenant roles).
  { path: 'admin/roles', element: RoleCatalog },
  // Sprint ELC-5.1 — department catalog (planning/staging for departments).
  { path: 'admin/departments', element: DepartmentCatalog },
  // FU5 — location catalog (flat list; HRIS-fed + manual).
  { path: 'admin/locations', element: LocationCatalog },
  // E3-FO-D2 — access catalog (what employees can request + how it is fulfilled).
  { path: 'admin/access-catalog', element: AccessCatalogAdmin },
  // Sprint ELC-6 W3 — post-action verification + drift admin queue.
  { path: 'admin/discrepancies', element: ElcDiscrepancies },
  // Sprint E3-M7 — per-role provisioning matrix.
  { path: 'admin/roles/:roleId/matrix', element: RoleMatrix },
  // Sprint CHRON-2 — Form Builder + FormList (admin only).
  { path: 'admin/forms', element: FormsAdmin },
];

export const routes = [
  ...ungatedRoutes,
  ...gatedRoutes.map((r) => ({ ...r, element: gated(r.element) })),
];
